<template>
  <v-container>
    <!-- ✅ Success Card -->
    <v-card
      v-if="savedLocation"
      outlined
      rounded="xl"
      class="pa-5 text-center elevation-2"
    >
      <v-icon color="success" large class="mb-3"
        >mdi-check-circle-outline</v-icon
      >
      <h2 class="font-weight-bold mb-2">Location Created Successfully</h2>
      <p class="grey--text text--darken-1">
        An employee with your name has been automatically created for this
        store. You can now log in to the store and continue setup.
      </p>
      <v-card-actions class="justify-center mt-4">
        <v-btn
          outlined
          color="primary"
          @click="$router.push({ path: '/location' })"
        >
          <v-icon left small>mdi-arrow-left</v-icon> Back to Locations
        </v-btn>
        <v-btn
          color="primary"
          dark
          rounded
          target="_blank"
          :href="`${terminalURL}/login/location?code=${savedLocation.code}`"
        >
          <v-icon left small>mdi-open-in-new</v-icon> Open New Location
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- ✅ Create Form -->
    <v-card v-else outlined rounded="xl" class="pa-6 elevation-1">
      <!-- Header -->
      <v-toolbar flat dense color="transparent" class="mb-4 pa-0">
        <v-toolbar-title>
          <h2 class="font-weight-bold">Business Information</h2>
          <div class="subtitle-2 grey--text text--darken-1 mt-1">
            Enter your business details below.
            <strong
              >This information will appear on your receipts and
              invoices.</strong
            >
          </div>
        </v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <!-- Business Info -->
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.name"
              label="Business Name"
              outlined
              :error-messages="locationNameErrors"
              hint="e.g. CellSmart Wireless LLC, El Primo Repairs"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.website"
              label="Website"
              outlined
              placeholder="https://example.com"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.DCALicense"
              label="DCA License"
              outlined
              placeholder="Optional"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.email"
              label="Email"
              outlined
              :error-messages="locationEmailErrors"
              placeholder="e.g. info@business.com"
            />
          </v-col>
        </v-row>

        <!-- Additional Info -->
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.type"
              label="Business Type"
              outlined
              placeholder="e.g. Retail, Auto Repair"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="Location.timeZone"
              :items="timeZones"
              item-text="label"
              item-value="tzCode"
              label="Time Zone"
              outlined
            />
          </v-col>
        </v-row>

        <!-- Address Section -->
        <v-divider class="my-6" />
        <h3 class="subtitle-1 font-weight-bold mb-3">Address Information</h3>

        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.type"
              label="Address Type"
              outlined
              placeholder="e.g. Business, Office, Home"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.phoneNumber"
              label="Phone Number"
              outlined
              placeholder="(555) 123-4567"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.addressLine1"
              label="Address Line 1"
              outlined
              :error-messages="addressLine1Errors"
              placeholder="Street address, P.O. box"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.addressLine2"
              label="Address Line 2"
              outlined
              placeholder="Apt, suite, unit, building, floor, etc."
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.city"
              label="City"
              outlined
              :error-messages="cityErrors"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="Location.Address.StateId"
              :items="states"
              item-text="name"
              item-value="id"
              label="State"
              outlined
              :error-messages="stateIdErrors"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Location.Address.postalCode"
              label="Zip / Postal Code"
              outlined
              :error-messages="postalCodeErrors"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-autocomplete
              v-model="Location.Address.CountryCode"
              :items="countries"
              item-text="name"
              item-value="code"
              label="Country"
              outlined
              :error-messages="countryCodeErrors"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="Location.Address.description"
              label="Description / Notes"
              outlined
              auto-grow
              placeholder="Any additional notes about this location"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider class="mt-3" />

      <v-card-actions class="d-flex justify-end py-4">
        <v-btn text color="grey darken-1" to="/location/list">
          <v-icon left>mdi-close</v-icon> Cancel
        </v-btn>
        <v-btn
          color="primary"
          dark
          rounded
          @click="submit"
          :loading="isLoading"
        >
          <v-icon left>mdi-content-save-outline</v-icon> Create Location
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import timezones from "compact-timezone-list";
import locationService from "../service.js";
const { validationMixin } = require("vuelidate");
const { required, minLength, email } = require("vuelidate/lib/validators");

export default {
  name: "location-create",
  data() {
    return {
      terminalURL: process.env.VUE_APP_TERMINALURL,
      isLoading: false,
      savedLocation: null,
      timeZones: timezones,
      Location: {
        name: null,
        type: null,
        website: null,
        DCALicense: null,
        timeZone: this.$moment.tz.guess(),
        email: null,
        Address: {
          type: null,
          addressLine1: null,
          addressLine2: null,
          city: null,
          StateId: null,
          CountryCode: null,
          postalCode: null,
          phoneNumber: null,
          description: null,
        },
      },
    };
  },
  computed: {
    ...mapGetters("global", ["states", "countries", "defaultCountryCode"]),
    locationNameErrors() {
      const errors = [];
      if (!this.$v.Location.name.$dirty) return errors;
      if (!this.$v.Location.name.required)
        errors.push("Business name is required.");
      if (!this.$v.Location.name.minLength)
        errors.push("Name must be at least 4 characters long.");
      return errors;
    },
    locationEmailErrors() {
      const errors = [];
      if (!this.$v.Location.email.$dirty) return errors;
      if (!this.$v.Location.email.email) errors.push("Invalid email address.");
      return errors;
    },
    addressLine1Errors() {
      const errors = [];
      if (!this.$v.Location.Address.addressLine1.$dirty) return errors;
      if (!this.$v.Location.Address.addressLine1.required)
        errors.push("Address is required.");
      if (!this.$v.Location.Address.addressLine1.minLength)
        errors.push("Address must have at least 2 characters.");
      return errors;
    },
    cityErrors() {
      const errors = [];
      if (!this.$v.Location.Address.city.$dirty) return errors;
      if (!this.$v.Location.Address.city.required)
        errors.push("City is required.");
      return errors;
    },
    postalCodeErrors() {
      const errors = [];
      if (!this.$v.Location.Address.postalCode.$dirty) return errors;
      if (!this.$v.Location.Address.postalCode.required)
        errors.push("Postal code is required.");
      return errors;
    },
    stateIdErrors() {
      const errors = [];
      if (!this.$v.Location.Address.StateId.$dirty) return errors;
      if (!this.$v.Location.Address.StateId.required)
        errors.push("State is required.");
      return errors;
    },
    countryCodeErrors() {
      const errors = [];
      if (!this.$v.Location.Address.CountryCode.$dirty) return errors;
      if (!this.$v.Location.Address.CountryCode.required)
        errors.push("Country is required.");
      return errors;
    },
  },
  methods: {
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal.fire({
          toast: true,
          icon: "error",
          title: "Please fix the highlighted fields.",
          position: "top-end",
          timer: 2500,
        });
        return;
      }

      this.isLoading = true;
      try {
        const response = await locationService.create(this.Location);
        this.savedLocation = response.data;
        this.$swal.fire({
          toast: true,
          icon: "success",
          title: "Location created successfully.",
          position: "top-end",
          timer: 3000,
        });
      } catch (error) {
        console.error(error);
        this.$swal.fire({
          toast: true,
          icon: "error",
          title: "Failed to create location. Please try again.",
          position: "top-end",
          timer: 3000,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  mixins: [validationMixin],
  validations: {
    Location: {
      name: { required, minLength: minLength(4) },
      email: { email },
      Address: {
        addressLine1: { required, minLength: minLength(2) },
        city: { required },
        postalCode: { required },
        StateId: { required },
        CountryCode: { required },
      },
    },
  },
};
</script>

<style scoped>
.v-card {
  transition: all 0.25s ease;
}
.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}
</style>